import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, User } from 'lucide-react';
import { navigation } from '../../config/navigation';
import { useNavigate, useLocation } from 'react-router-dom';
import Tooltip from './Tooltip';

interface SidebarProps {
  onSectionChange: (sectionId: string) => void;
  activeSection: string;
  activeArea: string;
}

export default function Sidebar({ onSectionChange, activeSection, activeArea }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeParent, setActiveParent] = useState<string | null>(activeSection);
  const [navigationProcessed, setNavigationProcessed] = useState<boolean>(false);

  // Check if we have state from navigation - only process once
  useEffect(() => {
    if (location.state?.section && location.state?.area && !navigationProcessed) {
      setActiveParent(location.state.section);
      onSectionChange(`${location.state.section}-${location.state.area}`);
      setNavigationProcessed(true);
      
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location, onSectionChange, navigationProcessed]);

  const handleItemClick = (item: typeof navigation[0]) => {
    if (item.id === activeSection) {
      // If clicking the same section, do nothing to prevent unnecessary reloads
      return;
    }
    
    if (item.areas) {
      if (activeParent !== item.id) {
        setActiveParent(item.id);
      }
      onSectionChange(`${item.id}-${item.areas[0].id}`);
    } else {
      onSectionChange(item.id);
    }
  };

  const handleAreaClick = (sectionId: string, areaId: string) => {
    if (activeSection === sectionId && activeArea === areaId) {
      // If clicking the same area, do nothing to prevent unnecessary reloads
      return;
    } else if (activeSection !== sectionId) {
      // If changing sections, update the active parent
      setActiveParent(sectionId);
    }
    
    onSectionChange(`${sectionId}-${areaId}`);
  };

  const currentNav = activeParent 
    ? navigation.find(item => item.id === activeParent)
    : null;

  return (
    <div 
      className={`h-screen fixed left-0 top-0 z-30 bg-white border-r border-gray-200 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="p-4 flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          {!isCollapsed && <span className="font-medium">Profile</span>}
        </div>

        <nav className="flex-1 px-2 py-4">
          {activeParent ? (
            <>
              <button
                onClick={() => setActiveParent(null)}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 text-gray-600 hover:bg-gray-50"
              >
                <ChevronLeft className="w-5 h-5" />
                {!isCollapsed && <span>Back</span>}
              </button>
              {currentNav?.areas?.map((area) => {
                const Icon = area.icon;
                return (
                  <Tooltip key={area.id} label={area.label}>
                    <button
                      onClick={() => handleAreaClick(currentNav.id, area.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 ${
                        activeSection === currentNav.id && activeArea === area.id
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {!isCollapsed && <span>{area.label}</span>}
                    </button>
                  </Tooltip>
                );
              })}
            </>
          ) : (
            navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Tooltip key={item.id} label={item.label}>
                  <button
                    onClick={() => handleItemClick(item)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 ${
                      activeSection === item.id
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </button>
                </Tooltip>
              );
            })
          )}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-center p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}